import { useQuery } from '@tanstack/react-query';
import {
  getDonorsCount,
  getDonationAmount,
  getRecentOrders,
  getOrders,
  getOrderDetails,
  getActiveSponsorshipsCount,
  getMonthlyDonationAmount,
  getPendingRemindersCount,
  getIncompleteSessions,
  getIncompleteSessionsCount,
  getRecurringDonations
} from 'lib/supabaseQueries';
import { useAuthContext } from 'context/authContext';
import useBranchPermission from './useBranchPermission';

// Stable fallback: a fresh [] each render makes react-table reset in a loop
const EMPTY_ARRAY = [];

// Hook to fetch total donors count
export function useDonorsCount(dateFilter) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donorsCount', organization?.id, branchId, dateFilter],
    queryFn: () => getDonorsCount(organization.id, branchId, dateFilter),
    enabled: isInitialized && !!organization?.id
  });

  return {
    count: data?.data ?? 0,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch total donation amount
export function useDonationAmount() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donationAmount', organization?.id, branchId],
    queryFn: () => getDonationAmount(organization.id, branchId, 'paid'),
    enabled: isInitialized && !!organization?.id
  });

  return {
    amount: data?.data ?? 0,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch recent orders
export function useRecentOrders(limit = 10) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['recentOrders', organization?.id, branchId, limit],
    queryFn: () => getRecentOrders(organization.id, branchId, limit),
    enabled: isInitialized && !!organization?.id
  });

  return {
    orders: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch all orders with optional filters
export function useOrders(filters = {}) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['orders', organization?.id, branchId, filters],
    queryFn: () => getOrders(organization.id, branchId, filters),
    enabled: isInitialized && !!organization?.id
  });

  return {
    orders: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch single order details
export function useOrderDetails(orderId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['orderDetails', orderId],
    queryFn: () => getOrderDetails(orderId),
    enabled: !!orderId
  });

  return {
    order: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch active sponsorships count
export function useActiveSponsorshipsCount() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['activeSponsorshipsCount', organization?.id, branchId],
    queryFn: () => getActiveSponsorshipsCount(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    count: data?.data ?? 0,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch monthly donation amount
export function useMonthlyDonationAmount() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['monthlyDonationAmount', organization?.id, branchId],
    queryFn: () => getMonthlyDonationAmount(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    amount: data?.data ?? 0,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch pending reminders count for current user
export function usePendingRemindersCount() {
  const { organization, user, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['pendingRemindersCount', organization?.id, branchId, user?.id],
    queryFn: () => getPendingRemindersCount(organization.id, branchId, user.id),
    enabled: isInitialized && !!organization?.id && !!user?.id
  });

  return {
    count: data?.data ?? 0,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch incomplete basket sessions
export function useIncompleteSessions() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['incompleteSessions', organization?.id, branchId],
    queryFn: () => getIncompleteSessions(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    sessions: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch recurring donations
export function useRecurringDonations(filters = {}) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['recurring-donations', organization?.id, branchId, filters],
    queryFn: () => getRecurringDonations(organization.id, branchId, filters),
    enabled: isInitialized && !!organization?.id
  });

  return {
    recurringDonations: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch incomplete sessions count
export function useIncompleteSessionsCount() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['incompleteSessionsCount', organization?.id, branchId],
    queryFn: () => getIncompleteSessionsCount(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    count: data?.data ?? 0,
    isLoading,
    error: error || data?.error,
    refetch
  };
}
